<script lang="ts" setup>
import { ChartOverlayTrigger } from '@forgd/ui-kit/charts/chart-overlay'
import { ChartTitle } from '@forgd/ui-kit/charts/chart-title'
import { chartColors } from '@forgd/ui-kit/charts/lib/colors'
import { useChartLogoPositions } from '#public-research/composables/useChartLogoPositions'
import { HIT_LAYER_COLOR, useRowHairline } from '#public-research/composables/useRowHairline'
import { calculateChartHeight } from '#public-research/utils/pr.utils'
import { formatValue } from '@forgd/formatter'
import { ExchangeAvatar } from '@forgd/ui-kit/blocks/avatars'
import { ColorKey, ColorKeyLabel, ColorKeyValue } from '@forgd/ui-kit/color-key'
import { Axis } from '@unovis/ts'
import { VisAxis, VisStackedBar, VisXYContainer } from '@unovis/vue'
import { breakpointsTailwind, useBreakpoints, useElementSize } from '@vueuse/core'

type ListingsByExchangeResponse = SuccessResponseBody<'/public-research/listings-by-exchange'>
type ListingByExchangeItem = ListingsByExchangeResponse['byExchange'][number]

const props = defineProps<{
  byExchange: ListingByExchangeItem[]
}>()

const legend: { label: string, color: string, key: keyof ListingByExchangeItem }[] = [
  {
    label: 'Primary (< 24 hours)',
    color: chartColors.tokenDesigner[0],
    key: 'primaryListingsCount',
  },
  {
    label: 'Follow-on (> 24 hours)',
    color: chartColors.tokenDesigner[1],
    key: 'followOnListingsCount',
  },
]

const chartData = computed(() =>
  props.byExchange.map((item, index) => ({ index, ...item })),
)
function yTickFormat(_: number | Date, index: number) {
  return props.byExchange[index]?.exchangeName ?? ''
}
const yTicks = computed(() => chartData.value?.map(item => item.index))

const xAccessor = (d: typeof chartData.value[number]) => d.index
const yAccessors = computed(() => [
  (d: typeof chartData.value[number]) => d.followOnListingsCount,
  (d: typeof chartData.value[number]) => d.primaryListingsCount,
])
const colors = computed(() => [
  chartColors.tokenDesigner[1],
  chartColors.tokenDesigner[0],
])
const legendContainer = useTemplateRef('legendContainer')
const { height: legendHeight } = useElementSize(legendContainer)

const MIN_ROW_HEIGHT = 22
const chartHeight = computed(() => {
  const base = calculateChartHeight({ legendHeight: legendHeight.value || 44, padding: 40 })
  const needed = chartData.value.length * MIN_ROW_HEIGHT
  return Math.max(base, needed)
})

const chartRow = useTemplateRef<HTMLElement>('chartRow')
const logoColumn = useTemplateRef<HTMLElement>('logoColumn')
const { logoPositions, logoItems } = useChartLogoPositions(chartData, chartHeight, chartRow, logoColumn)

const xDomain = computed(() => {
  const max = Math.max(...chartData.value.map(item => item.primaryListingsCount + item.followOnListingsCount))
  // slightly extend the domain, rounded to a clean integer
  return [0, Math.ceil(max * 1.05)]
})

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')
const xAxisNumTicks = computed(() => isMobile.value ? 4 : 7)

function xTickFormat(v: number | Date) {
  return formatValue(v, 'number:0')
}

const yAttributes = {
  [Axis.selectors.axis]: {
    'data-axis': 'y',
  },
}

const { chartRef, isHairlineVisible, hairlineStyle, onMouseMove, onMouseLeave } = useRowHairline()
const hitLayerYAccessors = computed(() => [() => xDomain.value[1]])

// Stable identity so hover-driven re-renders don't force a full unovis re-layout.
const chartMargin = { left: 8 }
</script>

<template>
  <div class="flex size-full flex-col">
    <ChartTitle text="Number of Listings | Exchange" />
    <div ref="chartRow" class="flex w-full">
      <div
        ref="logoColumn"
        class="relative mr-2 w-5 shrink-0"
        :style="{ height: `${chartHeight}px` }"
      >
        <div
          v-for="(item, idx) in logoItems"
          :key="item.exchangeName"
          class="absolute left-0 flex w-full -translate-y-1/2 items-center justify-center"
          :style="{ top: `${logoPositions[idx] ?? 0}px` }"
        >
          <ExchangeAvatar :src="item.logo" size="2xs" bordered />
        </div>
      </div>
      <div
        ref="chartRef"
        class="relative min-w-0 flex-1"
        @mousemove.capture="onMouseMove"
        @mouseleave="onMouseLeave"
      >
        <PrChartPlaceholder :height="chartHeight">
          <VisXYContainer :data="chartData" :x-domain="xDomain" :height="chartHeight" :margin="chartMargin">
            <VisStackedBar
              :x="xAccessor"
              :y="yAccessors"
              :color="colors"
              orientation="horizontal"
            />
            <VisStackedBar
              :x="xAccessor"
              :y="hitLayerYAccessors"
              :color="[HIT_LAYER_COLOR]"
              orientation="horizontal"
              :exclude-from-domain-calculation="true"
              duration="0"
            />
            <VisAxis
              type="x"
              :tick-format="xTickFormat"
              :num-ticks="xAxisNumTicks"
              :tick-line="false"
              :grid-line="true"
            />
            <VisAxis
              type="y"
              :tick-format="yTickFormat"
              :tick-values="yTicks"
              tick-text-align="left"
              tick-text-fit-mode="trim"
              tick-text-trim-type="end"
              :attributes="yAttributes"
              :tick-line="false"
              :grid-line="false"
            />
            <ChartOverlayTrigger>
              <template #default="{ d }">
                <div class="flex min-w-50 flex-col gap-2">
                  <div class="text-xs font-semibold capitalize">
                    {{ d.exchangeName }}
                  </div>
                  <ColorKey v-for="item in legend" :key="item.label" :color="item.color" layout="justify">
                    <ColorKeyLabel>{{ item.label }}</ColorKeyLabel>
                    <ColorKeyValue>{{ formatValue(d[item.key], 'number:0') }}</ColorKeyValue>
                  </ColorKey>
                </div>
              </template>
            </ChartOverlayTrigger>
          </VisXYContainer>
        </PrChartPlaceholder>
        <PrChartRowHairline :visible="isHairlineVisible" :style="hairlineStyle" />
      </div>
    </div>
    <div ref="legendContainer" class="mt-5 flex shrink-0 gap-2">
      <ColorKey v-for="item in legend" :key="item.label" :color="item.color" layout="inline" class="shrink-0">
        <ColorKeyLabel>{{ item.label }}</ColorKeyLabel>
      </ColorKey>
    </div>
  </div>
</template>

<style scoped>
.unovis-xy-container :deep([data-axis="y"] tspan) {
  text-transform: capitalize;
}
</style>
